import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import classNames from "classnames";
import { format } from "date-fns";
import { useForm } from "react-hook-form";

import Layout from "components/layout/Layout";
import { DashboardAppRow } from "components/dashboardAppRow/dashboardAppRow";
import { DashboardDocumentRow } from "components/dashboardDocumentRow/dashboardDocumentRow";
import NoDataMessage from "components/noDataBlock/noDataMessage";
import { Filter } from "components/UI/filter/filter";
import { SearchInput } from "components/UI/searchInput/searchInput";
import { Modal } from "components/modal/modal";
import Questionnaire, { QuestionnaireDataParams } from "components/questionnaire/questionnaire";
import { BackArrow } from "components/UI/backArrow/backArrow";
import SingleFilterDropDown, { OptionItem } from "components/UI/singleFilterDropDown/singleFilterDropDown";
import { Button } from "components/UI/button/button";
import FileUpload, { DocumentAdditionParams, FileUploadStyle } from "components/UI/FileUpload/fileUpload";
import { LoadingContainer } from "components/UI/loadingContainer/loadingContainer";
import loadingGif from "assets/images/mapleLeafLoad.gif";

import { useGetQuestionnaires, Questionnaire as QuestionnaireType } from "actions/questionnaireActions";
import { useGetApplicationInfo } from "actions/applicationsActions";
import {
  uploadSMBDocument,
  useGetDocuments,
  getSMBDocument,
  useGetDocumentNotifications,
  markNotificationsAsRead,
  SMBDocument,
} from "actions/documentActions";

import { fileToBase64, parseFileExtensions, IntStatus } from "utils/commonFunctions";
import { AllowedExtensions, SMBFileTypes, formatImage } from "utils/imageFunctions";

import useInfiniteScroll from "hooks/useInfiniteScroll";

import styles from "./appPage.module.scss";
import { DocumentFormFields } from "pages/documents/documents";
import { RoutePaths } from "app/routing/routing";
import DeleteTableRow from "components/icons/deleteTableRow";
import translations from "locales";

const filterOptions = [
  { id: "1", label: "Active" },
  { id: "4", label: "Approved" },
  { id: "0", label: "Get Started" },
  { id: "2", label: "Pending Review" },
  { id: "3", label: "Rejected" },
];

const sortOptions: OptionItem[] = [
  {
    label: "Status - Alphabetical (A to Z)",
    value: { orderBy: "status", sortOrder: "ASC" },
  },
  {
    label: "Status - Alphabetical (Z to A)",
    value: { orderBy: "status", sortOrder: "DESC" },
  },
];

const documentsFilterOptions = [
  { id: "IMAGE", label: "Images" },
  { id: "PDF", label: "PDF" },
  { id: "SPREADSHEET", label: "Spreadsheet" },
  { id: "DOCUMENT", label: "Word/Documents" },
  { id: "SHELL", label: "Other" },
];


const documentsSortOptions: OptionItem[] = [
  {
    label: "Date Uploaded - Newest to Oldest",
    value: { orderBy: "created", sortOrder: "DESC" },
  },
  {
    label: "Date Uploaded - Oldest to Newest",
    value: { orderBy: "created", sortOrder: "ASC" },
  },
  {
    label: "Document Name - Alphabetical (A to Z)",
    value: { orderBy: "docDesc", sortOrder: "ASC" },
  },
  {
    label: "Document Name - Alphabetical (Z to A)",
    value: { orderBy: "docDesc", sortOrder: "DESC" },
  },
  {
    label: "Matter Number - Newest to Oldest",
    value: { orderBy: "fileNo", sortOrder: "DESC" },
  },
  {
    label: "Matter Number - Oldest to Newest",
    value: { orderBy: "fileNo", sortOrder: "ASC" },
  },
];


export const Application = () => {
  // Router and navigation
  const { matterId, companyId, questionnaireId, contId } = useParams<{
    matterId: string;
    companyId: string;
    questionnaireId?: string;
    contId?: string;
  }>();

  const history = useHistory();
  const location = useLocation<{ fileDesc: string; fileNo: string }>();

  // Questionnaire Tab States
  const [activeTab, setActiveTab] = useState<"questionnaire" | "documents">("questionnaire");
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<QuestionnaireDataParams>();
  const [hideQuestionnaireModalHeader, setHideQuestionnaireModalHeader] = useState(false);
  const [isFilterDropdownVisible, setFilterDropdownVisible] = useState(false);
  const [searchWord, setSearchWord] = useState("");
  const [orderBy, setOrderBy] = useState("status");
  const [sortOrder, setSortOrder] = useState("");
  const [filter, setFilter] = useState<string[]>([]);

  // Documents Tab States
  const [docSearchWord, setDocSearchWord] = useState("");
  const [docOrderBy, setDocOrderBy] = useState("");
  const [docSortOrder, setDocSortOrder] = useState("");
  const [docType, setDocType] = useState<string[]>([]);
  const [isDocFilterDropdownVisible, setDocFilterDropdownVisible] = useState(false);
  const [showUploadDocumentsModal, setShowUploadDocumentsModal] = useState(false);
  const [showUploadDocumentsModalElements, setShowUploadDocumentsModalElements] = useState(true);
  const [selectedFileId, setSelectedFileId] = useState<string>(matterId || "");
  const [uploadQueue, setUploadQueue] = useState<{ files: File[]; success: boolean }>({
    files: [],
    success: false,
  });
  const [uploadMessages, setUploadMessages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0, percentage: 0 });
  const [failedUploads, setFailedUploads] = useState<Set<number>>(new Set());
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [smbDocument, setSmbDocument] = useState<SMBDocument | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [documentAccessLevel, setDocumentAccessLevel] = useState<number>(0); // 0 = Green and Spiegel only, 1 = Everyone on file
  const [pendingFiles, setPendingFiles] = useState<{ file: File; accessLevel: number }[]>([]); // Files selected but not yet uploaded

  // File upload and form
  const { register, formState: { errors }, setError, clearErrors } = useForm<DocumentFormFields>();
  const UPLOAD_FIELD_ID = "documentPageFileUpload" as keyof DocumentFormFields;

  // Data fetching hooks
  const { data: notificationData, mutate: mutateNotifications } = useGetDocumentNotifications();
  const { data: applicationInfo } = useGetApplicationInfo(matterId);
  const {
    data: questionnairesData,
    mutate: mutateQuestionnaires,
    noDataFound: noQuestionnairesDataFound,
  } = useGetQuestionnaires({ fileId: matterId, filter, orderBy, sortOrder, searchWord });
  const lang = localStorage.getItem('lang') || 'en';

  const {
    data: documentData,
    mutate: mutateDocuments,
    noDataFound: noDocumentDataFound,
    isValidating,
    size,
    setSize,
    hasMore,
  } = useGetDocuments({
    searchWord: docSearchWord,
    rowNumber: 100,
    orderBy: docOrderBy,
    sortOrder: docSortOrder,
    docType,
    fileId: matterId,
  });

  useEffect(() => {
    if (!questionnairesData.length || !questionnaireId) return;

    const questionnaire = questionnairesData.find(q =>
      q.questionnaireId === parseInt(questionnaireId) &&
      (!contId || q.contId === contId)
    );

    if (questionnaire) {
      onClickViewQuestionnaire(questionnaire);
    }
  }, [questionnairesData, questionnaireId, contId]);

  useEffect(() => {
    if (matterId) {
      setSelectedFileId(matterId);
    }
  }, [matterId]);

  const handleTabClick = async (tab: "questionnaire" | "documents") => {
    setActiveTab(tab);
    if (tab === "documents" && (notificationData?.numberOfNewDocuments ?? 0) > 0) {
      try {
        await markNotificationsAsRead();
        mutateNotifications({ numberOfNewDocuments: 0 }, false);
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
        mutateNotifications();
      }
    }
  };

  /* Handlers - Questionnaire Tab */
  const handleSortChange = (value: any) => {
    setOrderBy(value?.orderBy || "");
    setSortOrder(value?.sortOrder || "");
    const currentPath = location.pathname;
    const index = currentPath.indexOf(RoutePaths.Questionnaire);
    if (index !== -1) {
      history.push(currentPath.substring(0, index));
      setShowQuestionnaireModal(false);
    }
    mutateQuestionnaires();
  };

  const onSearch = (value: string) => {
    setSearchWord(value);
  };

  const onFilterChange = (selectedOptions: string[]) => {
    setFilter(selectedOptions);
  };

  const onClickViewQuestionnaire = (clickedQuestionnaire: QuestionnaireType) => {
    const path = `${location.pathname.split(RoutePaths.Questionnaire)[0]}${RoutePaths.Questionnaire}/${clickedQuestionnaire.questionnaireId}/${clickedQuestionnaire.contId}`;
    history.push(path);

    setSelectedQuestionnaire({
      title: clickedQuestionnaire.name,
      fileId: clickedQuestionnaire.fileId,
      questionnaireId: clickedQuestionnaire.questionnaireId,
      intStatus: clickedQuestionnaire.intStatus,
      contId: clickedQuestionnaire.contId
    });

    setShowQuestionnaireModal(true);
  };

  const closeQuestionnaire = () => {
    const currentPath = location.pathname;
    const index = currentPath.indexOf(RoutePaths.Questionnaire);
    const newPath = index !== -1 ? currentPath.substring(0, index) : currentPath;
    history.push(newPath);
    setShowQuestionnaireModal(false);
    setSelectedQuestionnaire(undefined);
  };

  const generateAddress = (appInfo: typeof applicationInfo) => {
    if (!appInfo?.streetName) return "n/a";
    const addressParts = [
      appInfo.streetName,
      appInfo.unitNumber,
      appInfo.city,
      appInfo.provinceState,
    ].filter(Boolean);
    return addressParts.join(", ");
  };

  /* Handlers - Documents Tab */
  const handleDocSortChange = (value: any) => {
    setDocOrderBy(value?.orderBy || "");
    setDocSortOrder(value?.sortOrder || "");
    resetDocData();
  };

  const handleDocFilterChange = (selectedOptions: string[]) => {
    setDocType(selectedOptions);
    resetDocData();
  };

  const onDocSearch = (value: string) => {
    setDocSearchWord(value);
    resetDocData();
  };

  const resetDocData = () => {
    setSize(1);
    mutateDocuments();
  };

  const openUploadDocumentsModal = () => {
    setUploadMessages([]);
    setPendingFiles([]);
    setShowUploadDocumentsModal(true);
    setShowUploadDocumentsModalElements(true);
    // Trigger file browser immediately when modal opens
    setTimeout(() => {
      const fileInput = document.getElementById(UPLOAD_FIELD_ID) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }, 1);
  };

  const handleCloseDocumentsModal = () => {
    setShowUploadDocumentsModal(false);
    setSelectedFileId(matterId || "");
    clearErrors(UPLOAD_FIELD_ID);
    setUploadQueue({ files: [], success: false });
    setDocumentAccessLevel(0);
    setPendingFiles([]);
    setUploadMessages([]);
    setUploadProgress({ completed: 0, total: 0, percentage: 0 });
    setFailedUploads(new Set());
  };

  const uploadDocument = async (file: File, accessLevel: number): Promise<{ messages: string[] }> => {
    if (!selectedFileId || !file) return { messages: [] };
    const rawBase64 = await fileToBase64(file);
    const base64RemoveExtension = rawBase64.split(",")[1];
    try {
      const response = await uploadSMBDocument(selectedFileId, file.name, base64RemoveExtension, undefined, accessLevel);
      return { messages: response.messages || [] };
    } catch (error) {
      return { messages: ["An error occurred during the upload. Please try again."] };
    }
  };


  const addDocuments = ({ files }: DocumentAdditionParams) => {
    try {
      clearErrors(UPLOAD_FIELD_ID);
      setUploadMessages([]);
      const { supportedFiles, error } = parseFileExtensions(files, [
        ...AllowedExtensions,
        SMBFileTypes.zipFile.value,
      ]);
      
      if (error) {
        setError(UPLOAD_FIELD_ID, error);
        return;
      }
      
      if (supportedFiles.length === 0) return;
      
      // Just store the files, don't upload yet
      setPendingFiles(prev => [...prev, ...supportedFiles.map(file => ({ file, accessLevel: documentAccessLevel }))]);
    } catch (error) {
      setError(UPLOAD_FIELD_ID, {
        type: "fileType",
        message: `File selection failed. Please try again.`,
      });
    }
  };

  const handleUploadDocuments = async () => {
    if (pendingFiles.length === 0) return;
    
    try {
      setIsUploading(true);
      setUploadMessages([]);
      setFailedUploads(new Set());
      setUploadProgress({ completed: 0, total: pendingFiles.length, percentage: 0 });
      
      const uploadResults: { success: boolean; messages: string[]; index: number }[] = [];
      const newFailedUploads = new Set<number>();
      
      // Upload files sequentially to track progress
      for (let i = 0; i < pendingFiles.length; i++) {
        const fileItem = pendingFiles[i];
        try {
          const result = await uploadDocument(fileItem.file, fileItem.accessLevel);
          const hasErrors = result.messages && result.messages.length > 0;
          
          uploadResults.push({
            success: !hasErrors,
            messages: result.messages || [],
            index: i
          });
          
          if (hasErrors) {
            newFailedUploads.add(i);
          }
          
          // Update progress
          const completed = i + 1;
          const percentage = Math.round((completed / pendingFiles.length) * 100);
          setUploadProgress({ completed, total: pendingFiles.length, percentage });
          
        } catch (error) {
          uploadResults.push({
            success: false,
            messages: ["Upload failed for this file. Please try again."],
            index: i
          });
          newFailedUploads.add(i);
          
          // Still update progress even for failed uploads
          const completed = i + 1;
          const percentage = Math.round((completed / pendingFiles.length) * 100);
          setUploadProgress({ completed, total: pendingFiles.length, percentage });
        }
      }
      
      setFailedUploads(newFailedUploads);
      
      const backendErrorMessages = uploadResults.flatMap((result) => result.messages || []);
      
      if (backendErrorMessages.length > 0) {
        setUploadMessages(backendErrorMessages);
        const successfulIndices = uploadResults
          .filter(result => result.success)
          .map(result => result.index);
        setPendingFiles(prev => prev.filter((_, index) => !successfulIndices.includes(index)));
      } else {
        // All uploads successful
        setUploadQueue({ files: [], success: true });
        setPendingFiles([]);
        setShowUploadDocumentsModalElements(false);
      }
    } catch (error) {
      setUploadMessages(["Upload failed. Please try again."]);
    } finally {
      setIsUploading(false);
      mutateDocuments();
    }
  };

  const removePendingFile = (indexToRemove: number) => {
    setPendingFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleDownload = async (doc: any) => {
    try {
      setIsDownloading(true);
      const smbDoc = await getSMBDocument(doc.docFileName);
      if (!smbDoc?.image) {
        console.error("SMB document or image not found");
        return;
      }
      const url = smbDoc.image.startsWith("blob:")
        ? smbDoc.image
        : URL.createObjectURL(
          new Blob(
            [
              smbDoc.format.startsWith("text/") || smbDoc.format.includes("json")
                ? smbDoc.image
                : new Uint8Array(
                  atob(smbDoc.image.split(",")[1])
                    .split("")
                    .map((c) => c.charCodeAt(0))
                ),
            ],
            { type: smbDoc.format }
          )
        );
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.docDesc || "document";
      link.click();
      if (!smbDoc.image.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const onClickDocument = async (doc: any) => {
    try {
      setIsLoadingPreview(true);
      setSelectedDocument(doc);
      setShowDocumentModal(true);
      const smbDoc = await getSMBDocument(doc.docFileName);
      setSmbDocument(smbDoc);
    } catch (error) {
      console.error("Failed to load document preview", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const image = formatImage(smbDocument?.format, smbDocument?.image);

  const loadMore = () => {
    setSize((prevSize) => prevSize + 1);
  };

  const [loader] = useInfiniteScroll(loadMore, hasMore, [
    docOrderBy,
    docSortOrder,
    docSearchWord,
    docType,
    activeTab,
  ]);

  return (
    <Layout>
      <div className={styles.applicationInfo}>
        <div className={styles.backArrow}>
          <BackArrow to={`${RoutePaths.Company}/${companyId}`} />
        </div>
        <h2>
          {location.state?.fileNo || applicationInfo?.fileNo} - {location.state?.fileDesc || applicationInfo?.fileDesc}
        </h2>
      </div>

      <div className={styles.tabsContainerWrapper}>
        <div className={styles.tabs}>
          <button
            className={classNames(styles.tab, activeTab === "questionnaire" ? styles.activeTab : styles.inactiveTab)}
            onClick={() => handleTabClick("questionnaire")}
          >
            <h5 className={styles.tabHeader}>{lang === 'fr' ? translations.fr.questionnaires : translations.en.questionnaires}</h5>
          </button>
          <button
            className={classNames(styles.tab, activeTab === "documents" ? styles.activeTab : styles.inactiveTab)}
            onClick={() => handleTabClick("documents")}
          >
            <h5 className={styles.tabHeader}>
              {(notificationData?.numberOfNewDocuments ?? 0) > 0 && (
                <span className={styles.notificationBadge}>
                  {notificationData?.numberOfNewDocuments}
                </span>
              )}
              {lang === 'fr' ? translations.fr.documents : translations.en.documents}
            </h5>
          </button>
        </div>
        <div className={styles.tabsUnderline}/>

        {activeTab === "questionnaire" && (
          <div className={styles.section}>
            <div className={styles.filterButtons}>
              <Filter
                id="filter-questionnaires"
                text={lang === 'fr' ? translations.fr.filter_questionnaires : translations.en.filter_questionnaires}
                mobileText="Filter"
                filterOptions={lang === 'fr' ? translations.fr.filterOptionsFr : filterOptions}
                className={styles.filter}
                onCheckboxChange={onFilterChange}
                isVisible={isFilterDropdownVisible}
                setVisible={setFilterDropdownVisible}
              />
              <SingleFilterDropDown
                id="sortQuestionnaires"
                label={lang === 'fr' ? translations.fr.sort_questionnaires : translations.en.sort_questionnaires}
                optionItems={lang === 'fr' ? translations.fr.sortOptionsFrApp : sortOptions}
                onOptionSelected={handleSortChange}
                mobileText="Sort"
                dropDownDirectionClass={styles.dropDownDirectionClass}
              />
              <SearchInput
                id="searchQuestionnaires"
                placeholder={lang === 'fr' ? translations.fr.try_questionnaire : translations.en.try_questionnaire}
                onChange={onSearch}
                className={styles.searchBar}
              />
            </div>

            <div className={styles.clientTableContainerWrapper}>
              {noQuestionnairesDataFound ? (
                <NoDataMessage title="No questionnaires available." />
              ) : (
                <div className={styles.clientTableContainer}>
                  <table className={classNames(styles.clientTable, styles.questionnairesTable)}>
                    <tbody>
                      {questionnairesData.map((row: QuestionnaireType, index: number) => {
                        return (
                          <DashboardAppRow
                            key={`questionnaire-${row.questionnaireId}-${row.contId}`}
                            index={index}
                            header={row.name}
                            progress={row.progress || 0}
                            intStatus={row.intStatus as IntStatus}
                            onClick={() => onClickViewQuestionnaire(row)}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Modal
              id="questionnaires"
              title={selectedQuestionnaire ? selectedQuestionnaire.title : ""}
              show={showQuestionnaireModal}
              onClose={closeQuestionnaire}
              className={styles.questionnaireModal}
              hideModalHeader={hideQuestionnaireModalHeader}
              key={`questionnaire-modal-${selectedQuestionnaire?.questionnaireId}-${selectedQuestionnaire?.contId}`}
            >
              <Questionnaire
                selectedQuestionnaire={selectedQuestionnaire as QuestionnaireDataParams}
                closeQuestionnaire={closeQuestionnaire}
                mutateQuestionnaires={mutateQuestionnaires}
                isReview={false}
                setHideQuestionnaireModalHeader={setHideQuestionnaireModalHeader}
                key={`questionnaire-${selectedQuestionnaire?.questionnaireId}-${selectedQuestionnaire?.contId}`}
              />
            </Modal>
          </div>
        )}

        {activeTab === "documents" && (
          <div className={styles.section}>
            <div className={styles.filterButtons}>
              <Button
                id="uploadDocuments"
                style="gradientAnimated"
                mobileText=""
                className={styles.uploadButton}
                onClick={openUploadDocumentsModal}
              >
                + {lang === 'fr' ? translations.fr.upload_documents : translations.en.upload_documents}
              </Button>
              <Filter
                id="filterDocuments"
                text={lang === 'fr' ? translations.fr.filter_documents : translations.en.filter_documents}
                filterOptions={lang === 'fr' ? translations.fr.documentsFilterOptionsFr : documentsFilterOptions}
                isVisible={isDocFilterDropdownVisible}
                onCheckboxChange={handleDocFilterChange}
                setVisible={setDocFilterDropdownVisible}
                className={styles.filter}
                mobileText={lang === 'fr' ? translations.fr.filter : translations.en.filter}
              />
              <SingleFilterDropDown
                id="sortDocuments"
                label={lang === 'fr' ? translations.fr.sort_documents : translations.en.sort_documents}
                optionItems={lang === 'fr' ? translations.fr.documentsSortOptionsFr : documentsSortOptions}
                onOptionSelected={handleDocSortChange}
                dropDownDirectionClass={styles.dropDownDirectionClass}
                mobileText={lang === 'fr' ? translations.fr.sort : translations.en.sort}
              />
              <SearchInput
                id="searchDocuments"
                placeholder={lang === 'fr' ? translations.fr.try_work_passport : translations.en.try_work_passport}
                onChange={onDocSearch}
                className={styles.searchBar}
              />
            </div>

            <div className={styles.documentContainerWrapper}>
              <div className={styles.tableScrollContainer}>
                <table className={classNames(styles.clientTable, styles.documentsTable)}>
                  <thead>
                    <tr>
                      <th>{lang === 'fr' ? translations.fr.document_name : translations.en.document_name}</th>
                      <th>{lang === 'fr' ? translations.fr.uploaded_by : translations.en.uploaded_by}</th>
                      <th>{lang === 'fr' ? translations.fr.uploaded_on : translations.en.uploaded_on}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentData &&
                      documentData.map((doc: any, index: number) => (
                        <DashboardDocumentRow
                          key={doc.docId}
                          index={index}
                          docDesc={doc.docDesc}
                          uploadedBy={doc.uploadedBy || "N/A"}
                          created={format(new Date(doc.created), "yyyy/MM/dd")}
                          onRowClick={() => onClickDocument(doc)}
                          onDownload={() => handleDownload(doc)}
                        />
                      ))}
                  </tbody>
                </table>
                {activeTab === "documents" && (
                  <div ref={loader} style={{ height: "1px", backgroundColor: "transparent" }} />
                )}
                {isValidating && documentData && size > 1 && <div>Loading more...</div>}
                {size === 1 && isValidating && (
                  <LoadingContainer loadingLabel="Loading documents..." />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        id="documentsUpload"
        title={lang === 'fr' ? translations.fr.upload_documents : translations.en.upload_documents}
        show={showUploadDocumentsModal}
        onClose={handleCloseDocumentsModal}
        headerClass={styles.modalHeader}
        className={styles.uploadDocumentModal}
        hideExitButton={false}
      >
        <div className={styles.modalContent}>
          {/* Show upload progress screen */}
          {isUploading && uploadProgress.total > 0 && (
            <div className={styles.progressContainer}>
              <div className={styles.progressRow}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressBarFill} 
                    style={{ width: `${uploadProgress.percentage}%` }}
                  />
                </div>
                <span className={styles.progressPercentage}>{uploadProgress.percentage}%</span>
              </div>
              <div className={styles.progressLabel}>Uploading Documents...</div>
            </div>
          )}

          {showUploadDocumentsModalElements && !isUploading && (
            <div className={styles.uploadDocumentContainer}>
              <div className={styles.accessLevelContainer}>
                <div className={styles.accessLevelRow}>
                  <div className={styles.accessLevelDropdown}>
                    <select 
                      value={documentAccessLevel} 
                      onChange={(e) => setDocumentAccessLevel(Number(e.target.value))}
                      className={styles.accessLevelSelect}
                    >
                      <option value={0}>Green and Spiegel</option>
                      <option value={1}>{lang === 'fr' ? translations.fr.everyone_on_file : translations.en.everyone_on_file}</option>
                    </select>
                    <span className={styles.accessLevelLabel}>{lang === 'fr' ? translations.fr.access_level : translations.en.access_level}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPendingFiles(prev => 
                        prev.map(item => ({ ...item, accessLevel: documentAccessLevel }))
                      );
                    }}
                    className={styles.applyToAllButton}
                    disabled={pendingFiles.length === 0}
                  >
                    {lang === 'fr' ? translations.fr.apply_to_all : translations.en.apply_to_all}
                  </button>
                </div>
              </div>
              
              <FileUpload
                id={UPLOAD_FIELD_ID}
                label="+ Select Documents"
                addDocuments={addDocuments}
                locked={isUploading}
                style={FileUploadStyle.BlueBubble}
                register={register}
                className={styles.hiddenFileUploadLabel}
              />

              {/* Display pending files as list */}
              {pendingFiles.length > 0 && (
                <div className={styles.selectedFilesContainer}>
                  <div className={styles.fileListContainer}>
                    <ul className={styles.fileList}>
                      {pendingFiles.map((fileItem, index) => (
                        <li key={index} className={classNames(
                          styles.fileListItem,
                          failedUploads.has(index) && styles.failedUpload
                        )}>
                          <span className={styles.fileName}>
                            {fileItem.file.name}
                            {failedUploads.has(index) && (
                              <span className={styles.failedIndicator}> (Upload Failed)</span>
                            )}
                          </span>
                          <div className={styles.fileControls}>
                            <select 
                              value={fileItem.accessLevel} 
                              onChange={(e) => {
                                const newAccessLevel = Number(e.target.value);
                                setPendingFiles(prev => 
                                  prev.map((item, idx) => 
                                    idx === index ? { ...item, accessLevel: newAccessLevel } : item
                                  )
                                );
                              }}
                              className={styles.fileAccessLevelSelect}
                              disabled={isUploading}
                            >
                              <option value={0}>Green and Spiegel</option>
                              <option value={1}>{lang === 'fr' ? translations.fr.everyone_on_file : translations.en.everyone_on_file}</option>
                            </select>
                            <button
                              type="button"
                              className={styles.deleteFileButton}
                              onClick={() => removePendingFile(index)}
                              disabled={isUploading}
                              title="Remove file"
                            >
                              <DeleteTableRow className={styles.deleteTableRow} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

            </div>
          )}

          <div className={styles.uploadTextContainer}>

            {/* Show success message if upload is complete */}
            {uploadQueue.success && !errors[UPLOAD_FIELD_ID] && !isUploading && (
              <p className={styles.uploadCompleteText}>Document Upload Complete!</p>
            )}

            {/* Show error message if there was an upload error */}
            {errors[UPLOAD_FIELD_ID] && (
              <p className={styles.error}>{errors[UPLOAD_FIELD_ID]?.message}</p>
            )}

            {/* Show upload messages if there were errors in uploaded files */}
            {uploadMessages && uploadMessages.length > 0 && (
              <div className={styles.uploadMessagesContainer}>
                <p className={styles.error}>
                  Parts of your zipped folder failed to upload! Some file types are not supported.
                </p>
                {uploadMessages.map((message, idx) => (
                  <p key={`uploadMessage_${idx}`} className={styles.error}>
                    {message}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Bottom button container */}
          {!isUploading && showUploadDocumentsModalElements && (
            <div className={styles.bottomButtonContainer}>
              <Button
                type="submit"
                style="gradient"
                className={styles.uploadButton}
                onClick={() => {
                  const fileInput = document.getElementById(UPLOAD_FIELD_ID) as HTMLInputElement;
                  if (fileInput) {
                    fileInput.click();
                  }
                }}
              >
                + {lang === 'fr' ? translations.fr.add_documents : translations.en.add_documents}
              </Button>
              
              {pendingFiles.length > 0 && (
                <Button
                  id="submitUpload"
                  onClick={handleUploadDocuments}
                  disabled={isUploading}
                  type="submit"
                  style="upload"
                  className={styles.uploadButton}
                > 
                  {lang === 'fr' 
                    ? (failedUploads.size > 0 
                      ? translations.fr.retry_upload
                      : translations.fr.submit_documents)
                    : (failedUploads.size > 0 
                      ? translations.en.retry_upload
                      : translations.en.submit_documents)}
                </Button>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        id="documentPreview"
        title={selectedDocument?.docDesc || "Loading document..."}
        show={showDocumentModal}
        onClose={() => {
          setShowDocumentModal(false);
          setSelectedDocument(null);
        }}
        className={styles.documentModal}
        hideModalHeader={false}
      >
        <div className={!isLoadingPreview ? styles.modalContent : styles.loadingContainer}>
          {isLoadingPreview ? (
            <LoadingContainer loadingLabel="Loading document preview..." />
          ) : (
            image
          )}
        </div>
        <div className={styles.modalFooter}>
          {!isLoadingPreview && (
            <Button
              id="downloadDocument"
              onClick={() => handleDownload(selectedDocument)}
              disabled={!smbDocument || isDownloading}
              style="gradient"
              className={styles.downloadButton}
            >
              {lang === 'fr' ? translations.fr.download_document : translations.en.download_document}
            </Button>
          )}
        </div>
      </Modal>
    </Layout>
  );
};
